import React, {Component, ReactNode} from 'react'
import {AsyncCommunicationProps, withAsyncCommunication} from '../../shared/hoc/withAsyncCommunication'
import EventEmitterExAdmin from '../../shared/pageBaseComponents/EventEmitterExAdmin'
import {AssocTerm} from '../model/AssocTerm'
import {AssocDirector} from '../model/AssocDirector'
import AssocDirectorRepository, {UpdateAssocDirectorInfo} from '../AssocDirectorRepository'
import {Information} from '../../../network/response/Information'
import {MESSAGE_TYPE} from '../../../CodeMaster'
import {AdminPageHeadingArea} from '../../shared/pageBaseComponents/AdminPageHeadingArea'
import { Box, DialogContent, DialogTitle, DialogActions } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import styled from 'styled-components'
import {LeftRowFlexContainer, RowFlexContainer} from '../../../styledComponents/FlexComponents'
import {Colors} from '../../../styledComponents/Constants'
import PPDatePicker from '../../shared/forms/PPDatePicker'
import {Nullable, Optional} from '../../../app/TypeHelpers'
import {AssocDirectorHelper} from '../utils/AssocDirectorHelper'
import {AdminPrimaryButton} from '../../../styledComponents/adminPage/Buttons'
import {BackLink} from '../../../styledComponents/adminPage/Links'
import NatAdminCommonAcceptedWarningComponent
    from '../../../__NoAutomatedTests/components/common/admin/NatAdminCommonAcceptedWarningComponent'
import {Warning} from '../../../__NoAutomatedTests/components/common/admin/NatAcceptedWarning'
import moment, {Moment} from 'moment'
import {formatDateLocal} from '../../../formatters/formatMoment'
import PPErrorLabel from '../../shared/forms/PPErrorLabel'
import {
    HeadingAreaMessageComponent,
    HeadingAreaMessageParams,
} from '../../shared/pageBaseComponents/HeadingAreaMessageComponent'
import {AssocOfCondominium} from '../model/AssocOfCondominium'

interface Props {
    condominiumCd: string
    selectedAssocId: number
    assocList: AssocOfCondominium[]
    assocTerm: AssocTerm
    prevAssocDirector: AssocDirector
    assocDirectorRepo: AssocDirectorRepository
    onSubmit: (information: Information) => void
    onCancel: () => void
    eventEmitter: EventEmitterExAdmin
}

interface State {
    availableStartDate: Nullable<Moment>
    availableEndDate: Nullable<Moment>
    warnings: Warning[]
    isAcceptedAllWarningOrNothingWarn: boolean
    errorHeadingAreaMessageParams: Nullable<HeadingAreaMessageParams>
}

class AdminAssocDirectorEditDialog extends Component<Props & AsyncCommunicationProps, State> {
    eventEmitterForDialog: EventEmitterExAdmin

    constructor(props: Readonly<Props & AsyncCommunicationProps>) {
        super(props)

        this.eventEmitterForDialog = new EventEmitterExAdmin()
        const {
            availableTermStartDate,
            availableTermEndDate,
        } = this.props.prevAssocDirector

        this.state = {
            availableStartDate: availableTermStartDate,
            availableEndDate: availableTermEndDate,
            warnings: [],
            isAcceptedAllWarningOrNothingWarn: true,
            errorHeadingAreaMessageParams: null,
        }
    }

    handleChangeAvailableTermStartDate = (date: Nullable<Date>) => {
        this.setState({availableStartDate: date ? moment(date.toISOString()) : null})
    }

    handleChangeAvailableTermEndDate = (date: Nullable<Date>) => {
        this.setState({availableEndDate: date ? moment(date.toISOString()) : null})
    }

    handleClickCancel = () => {
        this.props.onCancel()
    }

    handleClickSubmit = async () => {
        const {
            condominiumCd,
            prevAssocDirector,
            eventEmitter,
            asyncCallFunction,
            assocDirectorRepo,
            selectedAssocId,
            assocTerm,
            onSubmit,
        } = this.props
        const {
            availableStartDate,
            availableEndDate,
            warnings,
        } = this.state

        if (!availableStartDate || !availableEndDate) {
            return
        }

        const updateParam: UpdateAssocDirectorInfo = {
            availableTermStartDate: formatDateLocal(availableStartDate),
            availableTermEndDate: formatDateLocal(availableEndDate),
            assocDirectorVersion: String(prevAssocDirector.version),
            acceptedWarnings: warnings.map(warn => ({messageId: warn.messageId})),
        }

        this.setState({
                errorHeadingAreaMessageParams: null,
            },
        )
        this.eventEmitterForDialog.clearBroadcastedErrors()
        await eventEmitter.showLoading()

        try {
            const updateResult: Nullable<Information[]> = await asyncCallFunction(
                // @ts-ignore
                assocDirectorRepo.updateAssocDirector.bind(
                    assocDirectorRepo,
                    condominiumCd,
                    selectedAssocId,
                    assocTerm.assocTermId,
                    prevAssocDirector.assocDirectorId,
                    updateParam,
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.eventEmitterForDialog)
                    if (httpErrorOrNetworkError.responseBody.requestError !== null) {
                        const title = httpErrorOrNetworkError.responseBody.requestError.title
                        const message = httpErrorOrNetworkError.responseBody.requestError.message
                        const headingAreaMessageParams: HeadingAreaMessageParams = {
                            messageLevel: MESSAGE_TYPE.ERROR,
                            title: title,
                            detail: message,
                        }
                        this.setState({
                            errorHeadingAreaMessageParams: headingAreaMessageParams,
                        })
                    }
                })

            if (!updateResult || updateResult.length === 0) {
                return
            }

            const firstInformation = updateResult[0]

            if (firstInformation.type === MESSAGE_TYPE.WARNING) {
                this.setState({
                    warnings: updateResult.map(warn => ({
                        messageId: warn.messageId,
                        message: warn.message,
                    })),
                    isAcceptedAllWarningOrNothingWarn: false,
                })

                return
            }

            onSubmit(firstInformation)

        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleChangeEditDialogAcceptedWarning = (acceptAll: boolean) => {
        this.setState({isAcceptedAllWarningOrNothingWarn: acceptAll})
    }

    render(): ReactNode {
        const {
            availableStartDate,
            availableEndDate,
            warnings,
            isAcceptedAllWarningOrNothingWarn,
            errorHeadingAreaMessageParams,
        } = this.state
        const {prevAssocDirector, assocList, selectedAssocId, assocTerm} = this.props

        const maxOfStartDate: Optional<Date> = availableEndDate?.toDate()
        const minOfEndDate: Optional<Date> = availableStartDate?.toDate()

        const roomInfo = AssocDirectorHelper.getJoinedRoomNumberNodeByResidentStatus(prevAssocDirector.roomList)
        const userName = AssocDirectorHelper.getDisplayUserNameByResidentStatus(
            prevAssocDirector.familyName, prevAssocDirector.firstName, prevAssocDirector.roomList,
        )
        const selectedAssoc = assocList.find(assoc => assoc.assocId === selectedAssocId)
        if (!selectedAssoc) {
            throw Error('選択された管理組合がない')
        }

        const disabledEditButton = !availableStartDate || !availableEndDate || !isAcceptedAllWarningOrNothingWarn

        return <DialogArea>
            <StyledDialogTitle>
                <AdminPageHeadingArea
                    title={'役員編集'}
                />
                {errorHeadingAreaMessageParams &&
                    <HeadingAreaMessageComponent params={errorHeadingAreaMessageParams}/>
                }
            </StyledDialogTitle>
            <StyledDialogContents>
                <PrevAssocDirectorInfoArea>
                    {assocList.length > 1 &&
                        <div>
                            {selectedAssoc.assocName}
                        </div>
                    }
                    <div>
                        {AssocDirectorHelper.getDisplayAssocTerm(assocTerm)}
                    </div>
                    <Box height={'4px'}/>
                    <LeftRowFlexContainer>
                        <div>{roomInfo}</div>
                        <Box ml={'16px'}>{userName}</Box>
                    </LeftRowFlexContainer>
                </PrevAssocDirectorInfoArea>
                <Box height={'24px'}/>
                <span id={'input-term'}>
                    <ItemNameArea>役員向け機能の利用期間</ItemNameArea>
                    <ItemValueArea>
                        <ItemValueContainer>
                            <PPDatePicker
                                onChangeDate={this.handleChangeAvailableTermStartDate}
                                value={availableStartDate?.toDate() ?? null}
                                maxDate={maxOfStartDate}
                            />
                            <Box margin={'0 24px'}>〜</Box>
                            <PPDatePicker
                                onChangeDate={this.handleChangeAvailableTermEndDate}
                                value={availableEndDate?.toDate() ?? null}
                                minDate={minOfEndDate}
                            />
                        </ItemValueContainer>
                        <PPErrorLabel
                            eventEmitter={this.eventEmitterForDialog}
                            eventKeys={['availableTermStartDate', 'availableTermEndDate']}
                        />
                    </ItemValueArea>
                </span>
                {warnings.length > 0 && <Box height={'16px'}/>}
                <NatAdminCommonAcceptedWarningComponent
                    description={'内容をご確認いただき、問題なければチェックしたうえで再度登録するボタンを押してください。'}
                    warnings={warnings}
                    changeAcceptedWarnings={this.handleChangeEditDialogAcceptedWarning}
                    doOpenAnimation={true}
                />
            </StyledDialogContents>
            <StyledDialogActions>
                <PageFooterAreaLeft>
                    <BackLink onClick={this.handleClickCancel}>
                        キャンセル
                    </BackLink>
                </PageFooterAreaLeft>
                <PageFooterAreaRight>
                    <AdminPrimaryButton
                        onClick={this.handleClickSubmit}
                        disabled={disabledEditButton}
                    >
                        登録する
                    </AdminPrimaryButton>
                </PageFooterAreaRight>
            </StyledDialogActions>
        </DialogArea>
    }
}

export default withAsyncCommunication(AdminAssocDirectorEditDialog)

const PrevAssocDirectorInfoArea = styled.div`
  background: ${Colors.Background};
  padding: 8px 0 8px 8px;
  font-weight: bold;
  font-size: 16px;
`

const DialogArea = styled.div`
  width: 720px;
`

const StyledDialogTitle = withStyles({
    root: {
        padding: '24px 24px 0 24px',
    },
})(DialogTitle)

const StyledDialogContents = withStyles({
    root: {
        padding: '6px 24px 12px 24px',
    },
})(DialogContent)

const ItemNameArea = styled.div`
  font-size: 14px;
`

const ItemValueArea = styled.div`
  font-size: 16px;
  margin: 4px 0 0 16px;
`

const ItemValueContainer = styled.div<{ direction?: 'row' | 'column' }>`
  display: flex;
  flex-direction: ${props => props.direction ?? 'row'};
  justify-content: flex-start;
  align-items: ${props => props.direction === 'column' ? 'flex-start' : 'center'};

  .MuiCheckbox-root {
    padding: 5px 5px 5px 8px;
  }
`

const StyledDialogActions = withStyles({
    root: {
        padding: '12px 24px 24px',
    },
})(DialogActions)

const PageFooterAreaLeft = styled(RowFlexContainer)`
  justify-content: flex-start;
  flex-grow: 1;
  align-items: center;
  order: 1;
`

const PageFooterAreaRight = styled(RowFlexContainer)`
  justify-content: flex-end;
  flex-grow: 1;
  align-items: center;
  order: 2;
`
